import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, MapPin, Edit, Trash2, Copy, Eye, Search, LayoutGrid,
  List, MoreVertical, Users, ChevronRight, Loader2, Building,
  X, Map, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import ProducerLayout from '../../components/producer/ProducerLayout';
import VenueEditor from '../../components/venue/VenueEditor';

const ProducerVenuesPage = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showEditor, setShowEditor] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadVenues = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/venues/producer');
      setVenues(res.data.venues || []);
    } catch (error) {
      console.error('Error loading venues:', error);
      toast.error('Error al cargar los venues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  const handleCreateVenue = () => {
    setEditingVenue(null);
    setShowEditor(true);
  };

  const handleEditVenue = (venue) => {
    setEditingVenue(venue);
    setShowEditor(true);
  };

  const handleDuplicateVenue = async (venueId) => {
    try {
      await api.post(`/venues/${venueId}/duplicate`);
      toast.success('Venue duplicado exitosamente');
      loadVenues();
    } catch (error) {
      console.error('Error duplicating venue:', error);
      toast.error('Error al duplicar el venue');
    }
  };

  const handleDeleteVenue = async () => {
    if (!deleteConfirm) return;
    
    try {
      setDeleting(true);
      await api.delete(`/venues/${deleteConfirm.id}`);
      toast.success('Venue eliminado');
      setDeleteConfirm(null);
      loadVenues();
    } catch (error) {
      console.error('Error deleting venue:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el venue');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveVenue = (savedVenue) => {
    setShowEditor(false);
    setEditingVenue(null);
    loadVenues();
    toast.success(editingVenue ? 'Venue actualizado' : 'Venue creado exitosamente');
  };

  const filteredVenues = venues.filter(venue =>
    venue.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    venue.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTotalCapacity = (venue) => {
    return venue.sections?.reduce((sum, s) => sum + (s.capacity || 0), 0) || 0;
  };

  // Full screen editor
  if (showEditor) {
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950">
        <VenueEditor
          venue={editingVenue}
          onSave={handleSaveVenue}
          onCancel={() => {
            setShowEditor(false);
            setEditingVenue(null);
          }}
        />
      </div>
    );
  }

  return (
    <ProducerLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 flex items-center justify-center">
                <Map className="text-violet-400" size={20} />
              </div>
              Mis Venues
            </h1>
            <p className="text-zinc-400 mt-1">
              Crea y administra mapas de asientos reutilizables para tus eventos
            </p>
          </div>

          <Button
            onClick={handleCreateVenue}
            className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white shadow-lg shadow-violet-500/20"
          >
            <Plus size={18} className="mr-2" />
            Crear Venue
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                <Building className="text-violet-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{venues.length}</p>
                <p className="text-xs text-zinc-500">Total Venues</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                <LayoutGrid className="text-pink-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {venues.reduce((sum, v) => sum + (v.sections?.length || 0), 0)}
                </p>
                <p className="text-xs text-zinc-500">Secciones</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Users className="text-emerald-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {venues.reduce((sum, v) => sum + getTotalCapacity(v), 0).toLocaleString()}
                </p>
                <p className="text-xs text-zinc-500">Capacidad Total</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Calendar className="text-amber-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">0</p>
                <p className="text-xs text-zinc-500">En Uso</p>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar venues..."
              className="pl-10 bg-zinc-900 border-zinc-800 text-white"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-zinc-800' : ''}
            >
              <LayoutGrid size={16} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-zinc-800' : ''}
            >
              <List size={16} />
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-violet-500" size={32} />
          </div>
        ) : filteredVenues.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-zinc-800/50 flex items-center justify-center">
              <Map className="text-zinc-600" size={40} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {searchQuery ? 'Sin resultados' : 'Aún no tienes venues'}
            </h3>
            <p className="text-zinc-500 mb-6 max-w-md mx-auto">
              {searchQuery 
                ? 'No encontramos venues que coincidan con tu búsqueda'
                : 'Crea tu primer mapa de asientos para usar en tus eventos'
              }
            </p>
            {!searchQuery && (
              <Button
                onClick={handleCreateVenue}
                className="bg-gradient-to-r from-violet-600 to-pink-600"
              >
                <Plus size={18} className="mr-2" />
                Crear Primer Venue
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout">
              {filteredVenues.map((venue, index) => (
                <motion.div
                  key={venue.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                  className="group bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-violet-500/50 transition-all"
                >
                  {/* Preview Canvas */}
                  <div 
                    className="relative h-40 bg-zinc-800 overflow-hidden"
                    style={{
                      backgroundImage: venue.background_image ? `url(${venue.background_image})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {/* Mini map preview - centered */}
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                      <div 
                        className="relative"
                        style={{ 
                          width: venue.canvas_width || 800,
                          height: venue.canvas_height || 600,
                          transform: `scale(${Math.min(120 / (venue.canvas_width || 800), 120 / (venue.canvas_height || 600))})`,
                          transformOrigin: 'center center'
                        }}
                      >
                        {venue.sections?.map(section => (
                          <div
                            key={section.id}
                            className="absolute"
                            style={{
                              left: section.position?.x || 0,
                              top: section.position?.y || 0,
                              width: section.position?.width || 50,
                              height: section.position?.height || 50,
                              backgroundColor: `${section.color}60`,
                              border: `2px solid ${section.color}`,
                              borderRadius: section.shape === 'circle' ? '50%' : 
                                           section.shape === 'rounded' ? '12px' : 
                                           section.shape === 'hexagon' ? '8px' : '4px',
                              transform: section.rotation ? `rotate(${section.rotation}deg)` : 'none',
                              transformOrigin: 'center center'
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleEditVenue(venue)}
                        className="bg-white/10 hover:bg-white/20 text-white"
                      >
                        <Edit size={14} className="mr-1" /> Editar
                      </Button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-white group-hover:text-violet-400 transition-colors">
                          {venue.name}
                        </h3>
                        {venue.address && (
                          <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1">
                            <MapPin size={12} />
                            {venue.address}
                          </p>
                        )}
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                          <DropdownMenuItem 
                            onClick={() => handleEditVenue(venue)}
                            className="text-zinc-300 focus:bg-zinc-800"
                          >
                            <Edit size={14} className="mr-2" /> Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDuplicateVenue(venue.id)}
                            className="text-zinc-300 focus:bg-zinc-800"
                          >
                            <Copy size={14} className="mr-2" /> Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => setDeleteConfirm(venue)}
                            className="text-red-400 focus:bg-red-500/10"
                          >
                            <Trash2 size={14} className="mr-2" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-zinc-400">
                        <LayoutGrid size={14} className="inline mr-1" />
                        {venue.sections?.length || 0} secciones
                      </span>
                      <span className="text-zinc-400">
                        <Users size={14} className="inline mr-1" />
                        {getTotalCapacity(venue).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          // List View
          <div className="space-y-2">
            {filteredVenues.map((venue) => (
              <motion.div
                key={venue.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-violet-500/50 transition-all group"
              >
                <div 
                  className="w-16 h-16 rounded-lg bg-zinc-800 flex-shrink-0 relative overflow-hidden"
                  style={{
                    backgroundImage: venue.background_image ? `url(${venue.background_image})` : 'none',
                    backgroundSize: 'cover'
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Map className="text-zinc-600" size={24} />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-white truncate group-hover:text-violet-400 transition-colors">
                    {venue.name}
                  </h3>
                  <p className="text-sm text-zinc-500 truncate">
                    {venue.address || 'Sin dirección'}
                  </p>
                </div>

                <div className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
                  <span>
                    <LayoutGrid size={14} className="inline mr-1" />
                    {venue.sections?.length || 0}
                  </span>
                  <span>
                    <Users size={14} className="inline mr-1" />
                    {getTotalCapacity(venue).toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditVenue(venue)}
                    className="text-zinc-400 hover:text-white"
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDuplicateVenue(venue.id)}
                    className="text-zinc-400 hover:text-white"
                  >
                    <Copy size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteConfirm(venue)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Eliminar Venue</DialogTitle>
              <DialogDescription className="text-zinc-400">
                ¿Estás seguro de que deseas eliminar "{deleteConfirm?.name}"? 
                Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteVenue}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : (
                  <Trash2 size={16} className="mr-2" />
                )}
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ProducerLayout>
  );
};

export default ProducerVenuesPage;
